import { randomUUID } from "node:crypto";
import { Redis, SetCommandOptions } from "@upstash/redis";

const CHUNK_SIZE = 200;
const PIPELINE_CHUNK_SIZE = 100;

let redis: Redis;

type RedisPipeline = ReturnType<Redis["pipeline"]>;
type SetValueEntry = { key: string; value: string };
type RedisWithEval = Redis & {
  eval<TResult>(
    script: string,
    keys: string[],
    args: string[]
  ): Promise<TResult>;
};

export interface CacheLock {
  key: string;
  owner: string;
}

export interface AcquireCacheLockOptions {
  key: string;
  ttlSeconds: number;
  owner?: string;
}

export interface PollCacheLockValueOptions<T> {
  getValue: () => Promise<T | null | undefined>;
  maxWaitMs: number;
  /** Initial poll interval. Subsequent attempts double up to maxIntervalMs. */
  intervalMs: number;
  /** Optional ceiling on the doubled interval. Defaults to intervalMs (no growth). */
  maxIntervalMs?: number;
  isResolved?: (value: T | null | undefined) => value is T;
}

if (process.env.UPSTASH_REDIS_REST_URL != null) {
  try {
    redis = Redis.fromEnv();
  } catch (e) {
    console.error("REDIS_INIT_ERROR:: ", e);
  }
}

/**
 * Checks if the redis instance is configured
 *
 * ### Example
 * ```ts
 * CheckCommunications();
 * ```
 *
 * @throws Error if the redis instance is not configured
 * @returns void
 */
export function CheckCommunications(): void {
  if (process.env.UPSTASH_REDIS_REST_URL == null || redis == null) {
    throw new Error("REDIS_MISSING_CONFIG");
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Attempts to acquire a Redis-backed lock.
 *
 * The returned owner token must be passed back to ReleaseCacheLock so a request
 * cannot accidentally release a newer lock after its own TTL expired.
 */
export async function AcquireCacheLock({
  key,
  ttlSeconds,
  owner = randomUUID(),
}: AcquireCacheLockOptions): Promise<CacheLock | null> {
  try {
    CheckCommunications();

    const acquired = await redis.set(key, owner, {
      nx: true,
      ex: ttlSeconds,
    });

    return acquired ? { key, owner } : null;
  } catch (e) {
    console.error("REDIS_ACQUIRE_CACHE_LOCK_ERROR:: ", e);
  }

  return null;
}

/**
 * Releases a Redis-backed lock only if the stored owner still matches.
 */
export async function ReleaseCacheLock(lock: CacheLock): Promise<boolean> {
  try {
    CheckCommunications();

    const result = await (redis as RedisWithEval).eval<number>(
      `if redis.call("GET", KEYS[1]) == ARGV[1] then
        return redis.call("DEL", KEYS[1])
      end
      return 0`,
      [lock.key],
      [lock.owner]
    );

    return result === 1;
  } catch (e) {
    console.error("REDIS_RELEASE_CACHE_LOCK_ERROR:: ", e);
  }

  return false;
}

/**
 * Polls a caller-provided value checker until it returns a resolved value or
 * the bounded wait expires.
 */
export async function PollCacheLockValue<T>({
  getValue,
  maxWaitMs,
  intervalMs,
  maxIntervalMs,
  isResolved = (value): value is T => value !== null && value !== undefined,
}: PollCacheLockValueOptions<T>): Promise<T | null> {
  const initialInterval = Math.max(intervalMs, 1);
  const ceiling = Math.max(maxIntervalMs ?? initialInterval, initialInterval);
  const deadline = Date.now() + Math.max(maxWaitMs, 0);

  let nextInterval = initialInterval;
  while (true) {
    const value = await getValue();
    if (isResolved(value)) return value;

    const remaining = deadline - Date.now();
    if (remaining <= 0) return null;

    const waitMs = Math.min(nextInterval, remaining);
    await sleep(waitMs);
    nextInterval = Math.min(nextInterval * 2, ceiling);
  }
}

/**
 * Sets a value in the cache
 *
 * ### Example
 * ```ts
 * SetCache("MY_CACHE", "Frontend is awesome");
 *
 * // Sets a value for 1 hour
 * SetCache<MyObj>("MY_CACHE", { message: "Frontend is awesome"}, 3600);
 * ```
 *
 * @param key - String for the key to set the cache.
 * @param value - T (string by default) value to set in the cache.
 * @param expireInSeconds - Number of seconds to expire the cache. 0 or less will not expire.
 * @typeParam T - Type of the category; defaults to string.
 * @returns Promise<void>
 */
export async function SetCache<T = string>(
  key: string,
  value: T,
  expireInSeconds: number
): Promise<void> {
  try {
    CheckCommunications();

    let options: SetCommandOptions = {};

    if (expireInSeconds > 0) {
      options = {
        ex: expireInSeconds,
      };
    }

    await redis.set(key, value, options);
  } catch (e) {
    console.error("REDIS_SET_CACHE_ERROR:: ", e);
  }
}

/**
 * Gets a value from the cache
 *
 * ### Example
 * ```ts
 * GetCache("MY_CACHE", "Frontend is awesome");
 * // => "Frontend is awesome"
 *
 * GetCache<MyObj>("MY_CACHE", { message: "Frontend is awesome"});
 * // => { message: "Frontend is awesome"}
 * ```
 *
 * @param key - String for the key to get from the cache.
 * @param defaultValue - Default value to return if the key is not found.
 * @typeParam T - Type of the category; defaults to string.
 * @returns T - Value from the cache or the default value.
 */
export async function GetCache<T = string>(
  key: string,
  defaultValue: T
): Promise<T> {
  CheckCommunications();

  try {
    const cache = await redis.get<T>(key);
    if (cache !== null) {
      return cache as T;
    }
  } catch (e) {
    console.error("REDIS_GET_CACHE_ERROR:: ", e);
  }

  return defaultValue;
}

/**
 * Finds keys in the cache matching a pattern and returns the value of the first match
 *
 * ### Description
 * This function uses Redis pattern matching where * represents a wildcard that matches any characters.
 * For example, "mykey:1234:*:93929" would match "mykey:1234:anything:93929".
 *
 * ### Example
 * ```ts
 * FindClosest("mykey:1234:*:93929", "Default value");
 * // If "mykey:1234:something:93929" exists, it returns that value
 * // Otherwise returns "Default value"
 *
 * FindClosest<MyObj>("user:*:profile", { name: "Default" });
 * // If any key like "user:123:profile" exists, it returns that object
 * // Otherwise returns { name: "Default" }
 * ```
 *
 * @param pattern - Pattern string with wildcards (*) to search for in the cache.
 * @param defaultValue - Default value to return if no match is found.
 * @typeParam T - Type of the value; defaults to string.
 * @returns T - Value from the first matching key or the default value.
 */
export async function FindClosest<T = string>(
  pattern: string,
  defaultValue: T
): Promise<{ value: T; key: string | null }> {
  CheckCommunications();

  try {
    let cursor = "0";
    let matchingKeys: string[] = [];

    // SCAN may return no matches while the cursor is still not 0.
    do {
      const [nextCursor, page] = await redis.scan(cursor, {
        match: pattern,
        count: 100,
      });
      matchingKeys = page;
      cursor = String(nextCursor);
    } while (matchingKeys.length === 0 && cursor !== "0");

    if (matchingKeys.length > 0) {
      // Sort keys by length (shorter keys first) as a simple heuristic for "closest" match
      matchingKeys.sort((a, b) => a.length - b.length);

      // Get the first (shortest) matching key
      const matchedKey = matchingKeys[0];
      const value = await redis.get<T>(matchedKey);
      if (value !== null) {
        return {
          value: value as T,
          key: matchedKey,
        };
      }
    }
  } catch (e) {
    console.error("REDIS_FIND_CLOSEST_ERROR:: ", e);
  }

  return { value: defaultValue, key: null };
}

/**
 * Gets a value from the cache and if one is not provided set it for future calls to the cache.
 *
 * ### Description
 * This is useful for caching expensive operations that are not likely to change often.
 * i.e. fetching an auth token. Looking at you Salesforce.
 *
 * ### Example
 * ```ts
 * await UseSmartCache("MY_CACHE", async () => { return "Frontend is awesome" });
 * // => "Frontend is awesome"
 *
 * await UseSmartCache<MyObj, MyProps>(
 *  "MY_CACHE",
 *  async (props) => { return { message: props.message}},
 *  3600,
 *  { message: "Frontend is awesome"});
 * // => { message: "Frontend is awesome"}
 * ```
 *
 * @param key - String for the key to get from the cache.
 * @param getValueCallback - Async callback to get the value if one is not found in the cache.
 * @param props - Props to pass to the callback.
 * @param expireInSeconds - Number of seconds to expire the cache.
 * @typeParam T1 - Type of the value being cached; defaults to string.
 * @typeParam T2 - Type of the props being passed to the callback; defaults to void.
 * @returns T1 - Value from the cache or the value from the callback response.
 */
export async function UseSmartCache<T1 = string, T2 = void>(
  key: string,
  getValueCallback: (props: T2) => Promise<T1>,
  props: T2,
  expireInSeconds: number
): Promise<T1> {
  CheckCommunications();

  try {
    const cache = await redis.get<T1>(key);
    if (cache !== null) {
      return cache as T1;
    }
  } catch (e) {
    console.error("REDIS_GET_ELSE_SAVE_CACHE_ERROR:: ", e);
  }

  const value = await getValueCallback(props);
  await SetCache(key, value, expireInSeconds);
  return value;
}

/**
 * Adds a value to a set in the cache
 *
 * ### Description
 * A set is a collection of unique values, that are unordered. They are useful for storing a list of values that you want to ensure are unique.
 * Can be used to store a list of users, or a list of items in a shopping cart.
 *
 * ### Example
 * ```ts
 * await AddToSet("MY_CACHE", "Frontend is awesome");
 * await AddToSet("MY_CACHE", "No Backend is awesome");
 * ```
 *
 * @param key - String of the key for the set.
 * @param value - String of the value to add to the set.
 * @returns Promise<void>
 */
export async function AddToSet(key: string, value: string): Promise<void> {
  CheckCommunications();

  try {
    await redis.sadd(key, value);
  } catch (e) {
    console.error("REDIS_ADD_TO_SET_ERROR:: ", e);
  }
}

/**
 * Gets a value from a set in the cache
 *
 * ### Description
 * A set is a collection of unique values, that are unordered. They are useful for storing a list of values that you want to ensure are unique.
 * Can be used to store a list of users, or a list of items in a shopping cart.
 *
 * ### Example
 * ```ts
 * const setValues:string[] = await GetSet("MY_CACHE");
 * // => ["Frontend is awesome", "No Backend is awesome"]
 * ```
 *
 * @param key - String of the key for the set.
 * @returns string[] - Array of strings from the set.
 */
export async function GetSet(key: string): Promise<string[]> {
  CheckCommunications();

  try {
    const set = await redis.smembers(key);

    if (set != null) {
      return set;
    }

    return [];
  } catch (e) {
    console.error("REDIS_GET_SET_ERROR:: ", e);
  }

  return [];
}

/**
 * Gets values from multiple sets in the cache using pipelined SMEMBERS calls.
 *
 * @param keys - Strings of the keys for the sets.
 * @param chunkSize - Number of SMEMBERS commands per Redis pipeline.
 * @returns string[][] - Array of set members matching the order of the input keys.
 */
export async function GetSets(
  keys: string[],
  chunkSize = PIPELINE_CHUNK_SIZE
): Promise<string[][]> {
  CheckCommunications();
  const results: string[][] = [];

  for (const chunk of chunkArray(keys, chunkSize)) {
    try {
      const pipeline = redis.pipeline();
      chunk.forEach((key) => pipeline.smembers(key));
      const chunkResults = await pipeline.exec();
      results.push(...chunkResults.map(getStringArrayPipelineResult));
    } catch (e) {
      console.error("REDIS_GET_SETS_ERROR:: ", e);
      results.push(...chunk.map(() => []));
    }
  }

  return results;
}

/**
 * Removes a value from a set in the cache
 *
 * ### Description
 * A set is a collection of unique values, that are unordered. They are useful for storing a list of values that you want to ensure are unique.
 * Can be used to store a list of users, or a list of items in a shopping cart.
 *
 * ### Example
 * ```ts
 * await RemoveFromSet("MY_CACHE", "No Backend is awesome");
 * ```
 *
 * @param key - String of the key for the set.
 * @param value - String of the value to remove from the set.
 * @returns Promise<void>
 */
export async function RemoveFromSet(key: string, value: string): Promise<void> {
  CheckCommunications();

  try {
    await redis.srem(key, value);
  } catch (e) {
    console.error("REDIS_REMOVE_FROM_SET_ERROR:: ", e);
  }
}

/**
 * Adds values to sets in the cache using pipelined SADD calls.
 *
 * @param entries - Key/value pairs to add to Redis sets.
 * @param chunkSize - Number of SADD commands per Redis pipeline.
 * @returns Promise<void>
 */
export async function AddToSets(
  entries: SetValueEntry[],
  chunkSize = PIPELINE_CHUNK_SIZE
): Promise<void> {
  await runSetMutationPipeline(
    entries,
    (pipeline, entry) => pipeline.sadd(entry.key, entry.value),
    "REDIS_ADD_TO_SETS_ERROR",
    chunkSize
  );
}

/**
 * Removes values from sets in the cache using pipelined SREM calls.
 *
 * @param entries - Key/value pairs to remove from Redis sets.
 * @param chunkSize - Number of SREM commands per Redis pipeline.
 * @returns Promise<void>
 */
export async function RemoveFromSets(
  entries: SetValueEntry[],
  chunkSize = PIPELINE_CHUNK_SIZE
): Promise<void> {
  await runSetMutationPipeline(
    entries,
    (pipeline, entry) => pipeline.srem(entry.key, entry.value),
    "REDIS_REMOVE_FROM_SETS_ERROR",
    chunkSize
  );
}

/**
 * Checks if a value is in a set in the cache
 *
 * ### Description
 * A set is a collection of unique values, that are unordered. They are useful for storing a list of values that you want to ensure are unique.
 * Can be used to store a list of users, or a list of items in a shopping cart.
 *
 * ### Example
 * ```ts
 * const isInSet:boolean = await IsInSet("MY_CACHE", "My Frontend is awesome");
 * // => true
 * ```
 *
 * @param key  - String of the key for the set.
 * @param value  - String of the value to remove from the set.
 * @returns Promise<boolean>
 */
export async function IsInSet(key: string, value: string): Promise<boolean> {
  CheckCommunications();

  try {
    const set = await redis.sismember(key, value);
    return set === 1;
  } catch (e) {
    console.error("REDIS_GET_SET_ERROR:: ", e);
  }

  return false;
}

/**
 * Get all keys from Redis that match a pattern
 *
 * ### Description
 * This function uses Redis SCAN command to find all keys that match a given pattern.
 *
 * ### Example
 * ```ts
 * const keys = await GetAllKeys(redis, "my_pattern:*");
 * ```
 *
 * @param pattern the pattern to match keys against
 * @returns an array of matching keys
 */
export const GetAllKeys = async (pattern: string): Promise<string[]> => {
  const keys: string[] = [];
  let cursor = "0";

  do {
    const [nextCursor, page] = await redis.scan(cursor, {
      match: pattern,
      count: CHUNK_SIZE,
    });

    // SCAN may return no matches while the cursor is still not 0.
    if (page?.length) {
      keys.push(...page);
    }

    cursor = String(nextCursor);
  } while (cursor !== "0");

  return keys;
};

export const UnlinkKeys = async (keys: string[]): Promise<void> => {
  CheckCommunications();

  for (let i = 0; i < keys.length; i += CHUNK_SIZE) {
    const chunk = keys.slice(i, i + CHUNK_SIZE);
    try {
      await redis.unlink(...chunk);
    } catch (e) {
      console.error("REDIS_UNLINK_KEYS_ERROR:: ", e);
    }
  }
};

export const GetDataForKeys = async <T>(keys: string[]): Promise<T[]> => {
  CheckCommunications();
  const results: T[] = [];

  for (let i = 0; i < keys.length; i += CHUNK_SIZE) {
    const pipeline = redis.pipeline();
    const chunk = keys.slice(i, i + CHUNK_SIZE);

    chunk.forEach((key: string) => pipeline.get<T>(key));
    const chunkResults: T[] = await pipeline.exec();
    results.push(...chunkResults);
  }

  return results;
};

function chunkArray<T>(items: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];

  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }

  return chunks;
}

async function runSetMutationPipeline(
  entries: SetValueEntry[],
  addCommand: (pipeline: RedisPipeline, entry: SetValueEntry) => void,
  errorLabel: string,
  chunkSize: number
): Promise<void> {
  CheckCommunications();

  for (const chunk of chunkArray(entries, chunkSize)) {
    try {
      const pipeline = redis.pipeline();
      chunk.forEach((entry) => addCommand(pipeline, entry));
      await pipeline.exec();
    } catch (e) {
      console.error(`${errorLabel}:: `, e);
    }
  }
}

function getStringArrayPipelineResult(result: unknown): string[] {
  if (!Array.isArray(result)) {
    return [];
  }

  return result.filter((value): value is string => typeof value === "string");
}

export async function GetKeysWithDataAndTTL<T>(
  pattern: string
): Promise<Array<{ key: string; value: T | null; ttl: number }>> {
  let cursor = 0;
  const results: Array<{ key: string; value: T | null; ttl: number }> = [];

  try {
    do {
      const [nextCursor, keys] = await redis.scan(cursor, {
        match: pattern,
        count: 100,
      });

      if (keys.length > 0) {
        const pipeline = redis.pipeline();

        for (const key of keys) {
          pipeline.get<T>(key);
          pipeline.ttl(key);
        }

        const res = await pipeline.exec();

        for (let i = 0; i < keys.length; i++) {
          results.push({
            key: keys[i],
            value: res[i * 2] as T | null, // GET result
            ttl: res[i * 2 + 1] as number, // TTL result
          });
        }
      }

      cursor = Number(nextCursor);
    } while (cursor !== 0);
  } catch (e) {
    console.error("REDIS_GET_KEYS_WITH_DATA_AND_TTL_ERROR:: ", e);
  }

  return results;
}

export function GetRedisInstance(): Redis {
  CheckCommunications();

  return redis;
}

/**
 * Sets a single field in a Redis hash
 *
 * ### Example
 * ```ts
 * await SetHashField("my_hash", "fieldName", "value");
 * ```
 *
 * @param key - String of the hash key.
 * @param field - String of the field name within the hash.
 * @param value - String value to set.
 * @returns Promise<void>
 */
export async function SetHashField(
  key: string,
  field: string,
  value: string
): Promise<void> {
  CheckCommunications();

  try {
    await redis.hset(key, { [field]: value });
  } catch (e) {
    console.error("REDIS_SET_HASH_FIELD_ERROR:: ", e);
  }
}

/**
 * Gets all fields and values from a Redis hash
 *
 * ### Example
 * ```ts
 * const data = await GetHash("my_hash");
 * // => { fieldName: "value", ... }
 * ```
 *
 * @param key - String of the hash key.
 * @returns Record<string, string> - All fields and values, or an empty object.
 */
export async function GetHash(key: string): Promise<Record<string, string>> {
  CheckCommunications();

  try {
    const hash = await redis.hgetall<Record<string, string>>(key);
    return hash ?? {};
  } catch (e) {
    console.error("REDIS_GET_HASH_ERROR:: ", e);
  }

  return {};
}
